<template>
	<div>
		<!-- Sim Details Panel -->
		<base-header class="pb-6 bg-light">
			<!-- Page header -->
			<b-row aling-v="center" class="py-4">
				<b-col cols="12" lg="6">
					<h6 class="h2 d-inline-block mb-0 text-dark">
						SIM Details &nbsp; <span>{{ url }}</span
						>&nbsp;

						<!-- Show a state badge for the subscriber -->
						<base-button size="s" class="p-1 text-white" :class="stateBtn" :type="stateBtn">{{
							stateData
						}}</base-button>
					</h6>
				</b-col>
				<b-col cols="12" lg="6" class="text-right">
					<nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
						<route-bread-crumb></route-bread-crumb>
					</nav>
				</b-col>
			</b-row>

			<!-- SIM Statistics -->
			<b-row id="panelStatistics">
				<!--Data Sessions-->
				<b-col xl="3" lg="6" md="6">
					<statistics
						title="Data Sessions"
						cTitle="Current"
						dTitle="Daily"
						mTitle="Monthly"
						yTitle="Yearly"
						tabId="dataSession"
						type="gradient-info"
						color="bg-gradient-info"
						icon="fa fa-question"
            v-b-tooltip.hover.top="dataSessionToolTip"
            @changeSessionTab="changeSessionTab($event)"
						border="1"
					>
						<template slot="footer">
							<div class="text-center w-100">
								<b-icon
									icon="three-dots"
									class="text-center"
									animation="cylon"
									font-scale="4"
									v-if="!totalDataSessionValue && !dataSessionsFailedStatus"
								></b-icon>
							</div>

							<span v-if="totalDataSessionValue">
								<span class="h1 text-white font-weight-900 mb-0">{{ totalDataSessionValue }}</span>
								<br /><br />
								<span class="h3 text-white">Total Data Sessions</span>
								<span class="h4 text-white mr-2 float-right">{{ totalDataSessionPercent }}</span>
							</span>

							<div v-if="dataSessionsFailedStatus" class="text-center mt-2">
								<i class="fas fa-exclamation-triangle text-yellow" aria-hidden="true"></i><br />
								<span class="ml-2 mr-2 h4 text-white"
									>We were unable to load your data. Please contact support if you need assistance
									with this error.</span
								>
							</div>
						</template>
					</statistics>
				</b-col>

				<!--Data Volume-->
				<b-col xl="3" lg="6" md="6">
					<statistics
						title="Data Volume"
						cTitle="Current"
						dTitle="Daily"
						mTitle="Monthly"
						yTitle="Yearly"
						type="gradient-info"
						tabId="dataVolume"
						color="bg-gradient-success"
						icon="fa fa-question"
            v-b-tooltip.hover.top="dataVolumeByPeriodToolTip"
						@changeVolumeTab="changeVolumeTab($event)"
					>
						<template slot="footer">
							<div class="text-center w-100">
								<b-icon
									icon="three-dots"
									class="text-center"
									animation="cylon"
									font-scale="4"
									v-if="!totalDataVolumeValue && !dataVolumeFailedStatus"
								></b-icon>
							</div>
							<span v-if="totalDataVolumeValue">
								<span class="h1 text-white font-weight-900 mb-0">{{ totalDataVolumeValue }}</span>
								<br /><br />
								<span class="h3 text-white">Total Data Volume</span>
								<span class="h4 text-white mr-2 float-right">{{ totalDataVolumePercent }}</span>
							</span>
							<div v-if="dataVolumeFailedStatus" class="text-center mt-2">
								<i class="fas fa-exclamation-triangle text-yellow" aria-hidden="true"></i><br />
								<span class="ml-2 mr-2 h4 text-white"
									>We were unable to load your data. Please contact support if you need assistance
									with this error.</span
								>
							</div>
						</template>
					</statistics>
				</b-col>

				<!--Alerts-->
				<b-col xl="3" lg="6" md="6">
					<statistics
						title="Alerts"
						cTitle="Current"
						dTitle="Daily"
						mTitle="Monthly"
						yTitle="Yearly"
						type="gradient-info"
						tabId="alerts"
						color="bg-gradient-warning"
						icon="fa fa-question"
            v-b-tooltip.hover.top="alertsToolTip"
						@changeAlertsTab="changeAlertsTab($event)"
					>
						<template slot="footer">
							<div class="text-center w-100">
								<b-icon
									icon="three-dots"
									class="text-center"
									animation="cylon"
									font-scale="4"
									v-if="!totalAlertsValue && !alertsFailedStatus"
								></b-icon>
							</div>
							<span v-if="totalAlertsValue">
								<span class="h1 text-white font-weight-900 mb-0">{{ totalAlertsValue }}</span>
								<br /><br />
								<span class="h3 text-white">Total Alerts</span>
								<span class="h4 text-white mr-2 float-right">{{ totalAlertsPercent }}</span>
							</span>

							<div v-if="alertsFailedStatus" class="text-center mt-2">
								<i class="fas fa-exclamation-triangle text-yellow" aria-hidden="true"></i><br />
								<span class="ml-2 mr-2 h4 text-white"
									>We were unable to load your data. Please contact support if you need assistance
									with this error.</span
								>
							</div>
						</template>
					</statistics>
				</b-col>

				<!--Zero Session-->
				<b-col xl="3" lg="6" md="6">
					<statistics
						title="Zero Sessions"
						cTitle="Current"
						dTitle="Daily"
						mTitle="Monthly"
						yTitle="Yearly"
						type="gradient-info"
						tabId="zeroSessions"
						color="bg-gradient-danger"
						icon="fa fa-question"
            v-b-tooltip.hover.top="zeroSessionsToolTip"
            @changeZeroSessionsTab="changeZeroSessionsTab($event)"
					>
						<template slot="footer">
							<div class="text-center w-100">
								<b-icon
									icon="three-dots"
									class="text-center"
									animation="cylon"
									font-scale="4"
									v-if="!totalZeroSessionValue && !zeroSessionsFailedStatus"
								></b-icon>
							</div>
							<span v-if="totalZeroSessionValue">
								<span class="h1 text-white font-weight-900 mb-0">{{ totalZeroSessionValue }}</span>
								<br /><br />
								<span class="h3 text-white">Total Zero Sessions</span>
								<span class="h4 text-white mr-2 float-right">{{ totalZeroSessionPercent }}</span>
							</span>
							<div v-if="zeroSessionsFailedStatus" class="text-center mt-2">
								<i class="fas fa-exclamation-triangle text-yellow" aria-hidden="true"></i><br />
								<span class="ml-2 mr-2 h4 text-white"
									>We were unable to load your data. Please contact support if you need assistance
									with this error.</span
								>
							</div>
						</template>
					</statistics>
				</b-col>
			</b-row>

			<!-- SIM Detail [MODIFY] Panel -->
			<b-row id="panelSimDetails">
				<b-col cols="12">
					<!-- SIM Detail Card -->
					<SimDetailCard></SimDetailCard>
				</b-col>
			</b-row>
		</base-header>

		<!-- Sim Details Session Data -->
		<b-container fluid class="mt--6">
			<b-row>
				<b-col xl="5">
					<!-- Current Data Session Card -->
					<CurrentDataSession
						id="cardCurrentDataSession"
						title="Current Data Session"
						icon="fa fa-question"
            :tooltip="currentDataSessionToolTip"
          >
						<template slot="footer">
							<div class="text-center w-100">
								<b-icon
									icon="three-dots"
									class="text-center"
									animation="cylon"
									font-scale="4"
									v-if="currentDataSessionLoading"
								></b-icon>
							</div>
							<span v-if="!currentDataSessionLoading">
								<span class="h1" :class="sessionStatus">● </span>
								<span class="h4 mr-2 float-right"> Data: {{ sessionTime }}</span>
							</span>
						</template>
					</CurrentDataSession>

					<!-- State Card -->
					<State
						id="cardState"
						title="State"
						activity="Activate"
						modify="Modify"
						suspend="Suspend"
						resume="Resume"
						terminate="Terminate"
						icon="fa fa question"
						:imsi="imsi"
						:iccid="iccid"
            :tooltip="stateToolTip"
            :activeState="activeState"
						:modifyState="modifyState"
						:suspendState="suspendState"
						:resumeState="resumeState"
						:terminateState="terminateState"
						:isNsim="isNsim"
					>
						<template slot="footer">
							<div class="text-center w-100">
								<b-icon
									icon="three-dots"
									class="text-center"
									animation="cylon"
									font-scale="4"
									v-if="stateLoading"
								></b-icon>
							</div>
							<span v-if="!stateLoading">
								<span class="h1" :class="stateStatus">● </span>
								<span class="h4 mr-2 float-right">Service {{ stateData }}</span>
							</span>
						</template>
					</State>

					<!-- Data Management Card -->
					<!--            mPlan="Manage Plan"-->
					<DataManagement
						id="cardDataManagement"
						title="Data Management"
						alerts="Alerts"
						tabId="dataManagement"
						monitors="Data Monitors"
						dPlan="Data Plans"
						icon="fa fa-question"
						v-if="has_databuckets"
            :tooltip="dataManagementToolTip"
            @dataManagementTab="changeDataManagementTab($event)"
					>
						<template slot="footer">
							<div class="text-center w-100">
								<b-icon
									icon="three-dots"
									class="text-center"
									animation="cylon"
									font-scale="4"
									v-if="!dataManagementLoading"
								></b-icon>
							</div>
							<span v-if="!dataManagementLoading">
								<span class="h1" :class="sessionStatus">● </span>
								<span class="h4 mr-2 float-right">{{ sessionState }} : {{ sessionTime }}</span>
							</span>
						</template>
					</DataManagement>
				</b-col>

				<b-col xl="7">
					<!-- Data Volume Card -->
					<DataVolumeDetail
						id="cardDataVolumeByPeriod"
						title="Data Volume by Period"
						cTitle="Current"
						dTitle="Daily"
						mTitle="Monthly"
						yTitle="Yearly"
						type="gradient-info"
						icon="fa fa-question"
            :tooltip="dataVolumeByPeriodToolTip"
            @graphChangeTabByIMSI="getDataVolumeGraph($event)"
					>
					</DataVolumeDetail>

					<!-- Data Volume By Region Card (map) -->
					<data-volume-by-region
						id="cardDataVolumeByRegion"
						title="Data Volume by Region"
						cTitle="Current"
						dTitle="Daily"
						mTitle="Monthly"
						yTitle="Yearly"
						type="gradient-info"
						icon="fa fa-question"
            :tooltip="dataManagementToolTip"
            @changeDataRegionTab="getDataVolumeByRegion($event)"
					></data-volume-by-region>
				</b-col>
			</b-row>
		</b-container>
	</div>
</template>

<script>
import Statistics from "../Home/Statistics";
import SimInventory from "../Home/SimInventory";
import DataVolumeByRegion from "../Home/DataVolumeByRegion";
import RouteBreadCrumb from "../../../components/Breadcrumb/RouteBreadcrumb";
import DataVolumeTrend from "../Home/DataVolumeTrendsByPeriod";
import TopMetrics from "../Home/TopMetrics";
import cardOpenStatusDirective from "../../../common/directive/card-open-status-directive";
import SimDetailCard from "./SimDetailCard";
import searchDetailService from "@/common/api-services/search-details-service";
import configDirective from "../../../common/directive/config-directive";
import CurrentDataSession from "./CurrentDataSession";
import State from "./State";
import DataManagement from "./DataManagement";
import DataVolumeDetail from "./DataVolumeDetail";
import initService from "@/common/api-services/init-service";
import HomeDashboardService from "@/common/api-services/home-dashboard-service";
import searchDashboardService from "@/common/api-services/search-dashboard-service";
import homeDashboardService from "@/common/api-services/home-dashboard-service";

export default {
	name: "SimDetail",
	components: {
		DataVolumeDetail,
		CurrentDataSession,
		SimDetailCard,
		SimInventory,
		DataVolumeByRegion,
		RouteBreadCrumb,
		Statistics,
		DataVolumeTrend,
		TopMetrics,
		State,
		DataManagement,
	},
	data() {
		return {
			totalDataSessionValue: null,
			totalDataSessionPercent: null,
			totalDataVolumeValue: null,
			totalDataVolumePercent: null,
			totalAlertsValue: null,
			totalAlertsPercent: null,
			totalZeroSessionValue: null,
			totalZeroSessionPercent: null,
			chartXLabel: [],
			chartDataVolume: [],
			dataSessionToolTip: null,
			dataVolumeToolTip: null,
			alertsToolTip: null,
			zeroSessionsToolTip: null,
			dataVolumeByPeriodToolTip: null,
			dataVolumeByRegionToolTip: null,
			currentDataSessionToolTip: null,
			stateToolTip: null,
			dataManagementToolTip: null,
			currentDataSessionLoading: true,
			stateLoading: true,
			dataManagementLoading: true,
			sessionState: "loading",
			sessionTime: null,
			sessionStatus: null,
			stateData: "",
			stateStatus: "",
			stateBtn: "p-1 text-white bg-red",
			imsi: "",
			iccid: "",
			msisdn: "",
			activeState: false,
			modifyState: false,
			suspendState: false,
			resumeState: false,
			terminateState: false,
			url: "",
			isNsim: false,
			has_databuckets: false,
			zeroSessionsFailedStatus: false,
			dataVolumeFailedStatus: false,
			alertsFailedStatus: false,
			dataSessionsFailedStatus: false,
			isAdminUser: false,
			isPortalAdminUser: false,
			isReadOnlyUser: true, //default setting is readonly
		};
	},

	async mounted() {
		document.title = "Search - SIM Details";
		let config = configDirective.getConfigData();
		if (!config || config.length < 25) {
			await this.$router.push({name: "login"});
			sessionStorage.clear();
		}

		if (process.env.NODE_ENV === "dev") console.warn("[DEBUG][SimDetail.vue][mounted] START", Date());

		//set the defaults for the page
		this.initPage();

		//loads the default data
		this.reloadSimDetailPanel();

		//register tab clicked
    this.$root.$on("dataVolumeTrendByPeriodOpen", period => {
      this.getDataVolumeGraph(period);
      this.getDataVolumeAverage();
    });

		//register clicked
		this.$root.$on("dataVolumeTrendsByRegionOpen", period => {
			this.getDataVolumeByRegion(period);
		});

		//regster reload the page data
		this.$root.$on("reloadSimDetailPageData", imsi => {
			this.reloadSimDetailPanel();
		});

		//register set sim state for the vue
		this.$root.$on("setSimStatus", status => {
			this.setSimStatus(status);
		});

		// searchDetailService.getSimState(this.imsi).then(simInfo => {
		//   if (simInfo.msisdn && simInfo.msisdn.length >= 10) simInfo.status = 'Disabled';
		//   console.log('getSimState:', simInfo);
		//   this.stateStatus = simInfo.status;
		//   this.iccid = simInfo.ICCID;
		//   //this response has a lot more data needed for populating form.
		//
		//   if (this.stateStatus === 'Active' || this.stateStatus === 'Suspended') {
		//     this.reloadSimDetailPanel();
		//     this.getPageStatistics();
		//   } else {
		//     //this will prevent the page from loading everything via api
		//     this.stateLoading = false;
		//     this.setSimStatus(this.stateStatus);
		//   }
		// });

		if (process.env.NODE_ENV === "dev") console.warn("[DEBUG][SimDetail.vue][mounted] END", Date());
	},

	methods: {
		async initPage() {
			if (process.env.NODE_ENV === "dev") console.warn("[DEBUG][SimDetail.vue][initPage] START", Date());

			//check url for IMSI
			this.url = this.$route.query.imsi;
			if (!this.url || this.url.length !== 15) {
				this.$router.push({name: "Search"});
			} else {
				this.imsi = this.url;
			}

			//SET The Tab/Page Title
			document.title = "Search - SIM Details";

			//THESE ARE THE STANDARD ROLES FOR THE MENU
			this.isAdminUser = initService.getAdministratorRole();
			this.isPortalAdminUser = initService.getPortalAdminRole();
			this.isReadOnlyUser = initService.getReadOnlyRole();

			//show or hide data management
			if (configDirective.getConfigData()["site-settings"]["has_databuckets"]) {
				this.has_databuckets = true;
				//console.log('has_databuckets:', configDirective.getConfigData()['site-settings']['has_databuckets'])
			}

			//load the tooltips
			this.getSimDetailTooltips();

			if (process.env.NODE_ENV === "dev") console.warn("[DEBUG][SimDetail.vue][initPage] END", Date());
		},

		async reloadSimDetailPanel() {
			if (process.env.NODE_ENV === "dev")
				console.warn("[DEBUG][SimeDetail.vue][reloadSimDetailPanel] START...", Date());
			//this.$root.$emit('loadingPanelShow', true);

			//RELOAD ONLY THE BASICS
			let prevState = this.sessionState;

			//get the current sim state
			await this.getSimState(this.imsi);

			//TODO: STOP HIDING ALL STATES
			//if ((this.sessionState === 'Active' || this.sessionState === 'Suspended') && (prevState !== 'Active' && prevState !== 'Suspended')) {
			//get statistics
			this.getPageStatistics(this.imsi);
			//}

			if (prevState !== this.sessionState) {
				//get the data usage and plan
				if (this.has_databuckets) {
					this.getSimDetailPlanAndBalance(this.msisdn);
				}

				//lookup subscriber
				this.getSubscriberDetails(this.imsi);

				//get usage data
				this.getSimUsage(this.imsi);
			}

			//ALWAYS set the sim status
			this.setSimStatus(this.sessionState);

			//this.$root.$emit('loadingPanelHide', true);
			if (process.env.NODE_ENV === "dev")
				console.warn("[DEBUG][SimeDetail.vue][reloadSimDetailPanel] END...", Date());
		},

		async setSimStatus(status) {
			if (process.env.NODE_ENV === "dev")
				console.warn("[DEBUG][SimDetail.vue][setSimStatus] START [status]:", status, Date());
			//Inventory, Active, Disabled, Suspended, Billing-Hold, Ordered
			this.stateLoading = true;
			if (status === "Active") {
				this.stateStatus = "text-green";
				this.stateData = status;
				this.stateBtn = "p-1 text-white bg-green";
			} else if (status === "Pending") {
				this.stateStatus = "text-black";
				this.stateBtn = "p-1 text-white bg-black";
				this.stateData = status;
			} else if (status === "Suspended") {
				this.stateStatus = "text-black";
				this.stateBtn = "p-1 text-white bg-orange";
				this.stateData = status;
			} else if (status === "Inventory" || status === "Billing-Hold" || status === "Ordered") {
				this.stateStatus = "text-blue";
				this.stateBtn = "p-1 text-white bg-blue";
				this.stateData = status;
			} else {
				//Disabled or undefined
				this.stateStatus = "text-red";
				this.stateBtn = "p-1 text-white bg-red";
				this.stateData = "Disabled"; //default to disabled
			}

			//if user auth is read-only group disable all provisioning buttons
			if (this.isReadOnlyUser) {
				this.activeState = false;
				this.modifyState = false;
				this.suspendState = false;
				this.resumeState = false;
				this.terminateState = false;
			} else {
				if (status === "Active") {
					this.activeState = false;
					this.modifyState = true;
					this.suspendState = true;
					this.resumeState = false;
					this.terminateState = true;
				} else if (status === "Pending") {
					this.activeState = false;
					this.modifyState = false;
					this.suspendState = false;
					this.resumeState = false;
					this.terminateState = false;
				} else if (status === "Suspended") {
					this.activeState = false;
					this.modifyState = false;
					this.suspendState = false;
					this.resumeState = true;
					this.terminateState = false;
				} else {
					//Inventory, Disabled, Billing-Hold, Ordered
					this.activeState = true;
					this.modifyState = false;
					this.suspendState = false;
					this.resumeState = false;
					this.terminateState = false;
				}
			}

			let list = [
				"panelSimDetails",
				"panelStatistics",
				"cardCurrentDataSession",
				"cardDataVolumeByPeriod",
				"cardDataVolumeByRegion",
				//"cardDataManagement",
			];

			//TODO: STOP HIDING ALL STATES
			/*   NO LONGER HIDING CARDS
      let style = 'none';
      if (status === "Active" || status === "Suspended") {
        style = '';
      } else {
        console.warn('This SIM is not in an active state. No data about this SIM is available.');
      }
      for (var i = 0; i < list.length; i++) {
        document.getElementById(list[i]).style.display = style;
      }
      //toggle this only if it is enabled.
      if (this.has_databuckets) document.getElementById('cardDataManagement').style.display = style;

      //TODO: note, i haven't decided if we should hide this or not.
      // if (status === "Suspended") {
      //   document.getElementById("panelSimDetails").style.display = 'none';
      // }

      */

			this.stateLoading = false;
			this.currentDataSessionLoading = false;
			this.dataManagementLoading = false;

			if (process.env.NODE_ENV === "dev") console.warn("[DEBUG][SimeDetail.vue][setSimStatus] END...", Date());
		},

		async getPageStatistics(imsi) {
			if (process.env.NODE_ENV === "dev") console.warn("[DEBUG][SimDetail.vue][getPageStatistics] START", Date());
			this.getDataSessionsByIMSI("current");
			this.getDataVolumeByIMSI("current");
			this.getAlertsByIMSI("current");
			this.getZeroSessionsByIMSI("current");
			this.getCurrentDataSession();
			this.getDataVolumeGraph("Current");
			this.getDataVolumeAverage();
			this.changeDataManagementTab("Alerts");
			this.getDataVolumeByRegion("current");
			this.getImeiData(imsi);
			if (process.env.NODE_ENV === "dev") console.warn("[DEBUG][SimDetail.vue][getPageStatistics] END", Date());
		},

		async getSimState(imsi) {
			if (process.env.NODE_ENV === "dev") console.warn("[DEBUG][SimeDetail.vue][getSimState] START...", Date());
			await searchDetailService.getSimState(imsi).then(res => {
				// console.log("getSimState:", res)
				if (!res || !res.status) {
					res = [];
					res["status"] = "Disabled";
					res["iccid"] = "";
					res["msisdn"] = "";
				}
				// console.warn('Your SIM is in an [', res.status, '] state.');
				this.sessionState = res.status;
				this.iccid = res.ICCID;
				this.msisdn = res.MSISDN;
				//console.error('[1]GET SIM STATE simInfoLoad ',JSON.stringify(res))
				this.$root.$emit("simInfoLoad", res);
				this.setSimStatus(res.status);
			});
			if (process.env.NODE_ENV === "dev") console.warn("[DEBUG][SimeDetail.vue][getSimState] END...", Date());
		},

		//GET USAGE
		async getSimUsage(imsi) {
			//IOT_GetSmsUsage($imsi, $maxrecords)
			await searchDashboardService.getSmsUsageByIMSI(this.imsi).then(res => {
				let usage = [];
				usage.sms = "0 MSG";
				if (res) usage.sms = String(res);
				this.$root.$emit("simUsage", usage);
			});
			//IOT_GetTotalDataVolumesByPeriod&period=monthly&imsi=311560000016656
			await homeDashboardService.getDataVolumeStatistic(this.imsi, "monthly").then(res => {
				let usage = [];
				usage.data = "0 MB";
				if (res["Total Data Volumes"]) usage.data = String(res["Total Data Volumes"]);
				this.$root.$emit("simUsage", usage);
			});
		},

		//LOOKUP SUBSCRIBER
		async getSubscriberDetails(imsi) {
			if (process.env.NODE_ENV === "dev")
				console.warn("[DEBUG][SimeDetail.vue][getSubscriberDetails] START...", Date());

			//this.stateLoading = true;

			searchDashboardService.getLookupSubscriberByIMSI(imsi).then(res => {
				//if (!res.success.results.length) {
				if (String(res).includes("FAIL")) {
					if (process.env.NODE_ENV === "dev") {
						console.warn("No results found for this IMSI.");
					}
					this.$toast.error("No results found for this IMSI.");
				}
				if (res.success.results) {
					// console.log("Subscriber Details: ", res.success.results)
					let load = structuredClone(res.success.results[0]);
					// console.log("Primairy_SIM", load)
					if (res.success.results.length > 1 && res.success.results[0].hasOwnProperty("Type") && res.success.results[0].Type.toLowerCase() === "primary") {
						if (this.url === res.success.results[1].IMSI) {
							// console.log("RECALL for primary IMSI")
							this.isNsim = true;
							// Get Primary IMSI Data
							this.getImeiData(load.IMSI);
							// Reload Sim Detail Panel as Primary IMSI
							// if (this.imsi !== load.IMSI) {
							// 	this.reloadSimDetailPanel();
								this.imsi = load.IMSI;
							// 	return;
							// }
						}
						if (res.success.results[1].hasOwnProperty("Type")) {
							if (res.success.results[1].Type.toLowerCase() === "roaming") {
								// console.log("Roaming IMSI: ", res.success.results[1]["IMSI"])
								load.RoamingIMSI = res.success.results[1].IMSI;
								load.RoamingStatus = res.success.results[1]["Status"];
								load.TIS_Status = res.success.results[1]["TIS_Roaming"];
								load.url = this.url;
							}
						}
					}
					this.iccid = load.ICCID;
					this.$root.$emit("simDetailLookSubscriber", load);
				} else {
					this.$toast.error(res.success);
					this.$root.$emit("simDetailLookSubscriber", {});
				}

				//   this.stateLoading = false;
				//} else {
				//     console.error('Unable to retreive the status of this IMSI. See: ', res);
				//     this.$root.$emit('simDetailFailedResponse', true);
				//}
				// try {
				//   if (this)
				//   this.setSimStatus(res.success.results[0].Status);
				// } catch (e) {
				//   console.error(e);
				//   console.error('Unable to retreive the status of this IMSI. See: ', res);
				//   this.$root.$emit('simDetailFailedResponse', true);
				//   this.setSimStatus('Error');
				// }

				// if (!Array.isArray(res.success.results) || !res.success.results.length) {
				//   //fix the status
				//   // if (res.success.results[0].Status==='Active' && res.success.results[0].MSISDN ==='' ) {
				//   //   res.success.results[0].Status==='Disabled';
				//   // }
				//   if (process.env.NODE_ENV === 'dev') console.warn('[DEBUG][SimDetail.vue][getSubscriberDetails] res.success.results: STATUS', res.success.results);
				//   this.setSimStatus(res.success.results[0].Status);
				// } else {
				//   console.error('Unable to retreive the status of this IMSI. See: ', res);
				//   this.$root.$emit('simDetailFailedResponse', true);
				// }
			});

			if (process.env.NODE_ENV === "dev")
				console.warn("[DEBUG][SimeDetail.vue][getSubscriberDetails] END...", Date());
		},

		async getSimDetailPlanAndBalance(msisdn) {
			if (process.env.NODE_ENV === "dev")
				console.warn("[DEBUG][SimeDetail.vue][getSimDetailPlanAndBalance] START...", Date());
			searchDetailService.getSimDetailPlanAndBalance(msisdn).then(res => {
				this.$root.$emit("simDetailDataInfo", res);
				if (process.env.NODE_ENV === "dev")
					console.warn("[DEBUG][SimeDetail.vue][getSimDetailPlanAndBalance] END...", Date());
			});
		},

		async getImeiData(imsi) {
			if (process.env.NODE_ENV === "dev") console.warn("[DEBUG][SimeDetail.vue][getImeiData] START...", Date());
			searchDetailService.getlmeiDataByIMSI(imsi).then(res => {
				this.$root.$emit("simDetailOperator", res);
				if (process.env.NODE_ENV === "dev") console.warn("[DEBUG][SimeDetail.vue][getImeiData] END...", Date());
			});
		},

		async getDataSessionsByIMSI(period) {
			if (process.env.NODE_ENV === "dev")
				console.warn("[DEBUG][SimeDetail.vue][getDataSessionsByIMSI] START...", Date());

			this.totalDataSessionValue = null;
			this.dataSessionsFailedStatus = false;
			HomeDashboardService.getDataSessionStatistic(this.imsi, period).then(res => {
				if (res !== "FAIL - API Request Failed." && res !== undefined) {
					this.totalDataSessionValue = res["Total Data Sessions"];
					this.totalDataSessionPercent = res["Percentage Change"];
				} else {
					this.dataSessionsFailedStatus = true;
				}
				if (process.env.NODE_ENV === "dev")
					console.warn("[DEBUG][SimeDetail.vue][getDataSessionsByIMSI] END...", Date());
			});
		},

		async getDataVolumeByIMSI(period) {
			if (process.env.NODE_ENV === "dev")
				console.warn("[DEBUG][SimeDetail.vue][getDataVolumeByIMSI] START...", Date());
			this.totalDataVolumeValue = null;
			this.dataVolumeFailedStatus = false;
			HomeDashboardService.getDataVolumeStatistic(this.imsi, period).then(res => {
				if (res !== "FAIL - API Request Failed." && res !== undefined) {
					this.totalDataVolumeValue = res["Total Data Volumes"];
					this.totalDataVolumePercent = res["Percentage Change"];
				} else {
					this.dataVolumeFailedStatus = true;
				}
				if (process.env.NODE_ENV === "dev")
					console.warn("[DEBUG][SimeDetail.vue][getDataVolumeByIMSI] END...", Date());
			});
		},

		async getAlertsByIMSI(period) {
			if (process.env.NODE_ENV === "dev") console.warn("[DEBUG][SimDetail.vue][getAlertsByIMSI] START", Date());
			this.totalAlertsValue = null;
			this.alertsFailedStatus = false;
			HomeDashboardService.getAlertsStatistic(this.imsi, period).then(res => {
				if (res !== "FAIL - API Request Failed." && res !== undefined) {
					this.totalAlertsValue = res["Total Alerts"];
					this.totalAlertsPercent = res["Percentage Change"];
				} else {
					this.alertsFailedStatus = true;
				}
				if (process.env.NODE_ENV === "dev") console.warn("[DEBUG][SimDetail.vue][getAlertsByIMSI] END", Date());
			});
		},

		async getZeroSessionsByIMSI(period) {
			if (process.env.NODE_ENV === "dev")
				console.warn("[DEBUG][SimeDetail.vue][getZeroSessionsByIMSI] START...", Date());
			this.totalZeroSessionValue = null;
			this.zeroSessionsFailedStatus = false;
			HomeDashboardService.getZeroSessionStatistic(this.imsi, period).then(res => {
				if (res !== "FAIL - API Request Failed." && res !== undefined) {
					this.totalZeroSessionValue = res["Total Zero Sessions"];
					this.totalZeroSessionPercent = res["Percentage Change"];
				} else {
					this.zeroSessionsFailedStatus = true;
				}
				if (process.env.NODE_ENV === "dev")
					console.warn("[DEBUG][SimeDetail.vue][getZeroSessionsByIMSI] END...", Date());
			});
		},

		async getCurrentDataSession() {
			if (process.env.NODE_ENV === "dev")
				console.warn("[DEBUG][SimeDetail.vue][getCurrentDataSession] START...", Date());

			searchDetailService.getCurrentDataSessionByIMSI(this.imsi).then(res => {
				this.currentDataSessionLoading = false;
				if (res && res["session_state"] !== "undefined" && typeof res["session_state"]) {
					this.sessionState = res["session_state"];
					this.sessionTime = res["session_time"];
					if (this.sessionState === "Current Session") {
						this.sessionStatus = "text-success";
					} else {
						this.sessionStatus = "text-warning";
					}
				} else {
					this.sessionStatus = "text-warning";
					this.sessionState = "No Active Session";
				}
				if (process.env.NODE_ENV === "dev")
					console.warn("[DEBUG][SimeDetail.vue][getCurrentDataSession] END...", Date());
			});
		},

		async getDataVolumeGraph(period) {
			if (process.env.NODE_ENV === "dev") {
				console.warn("[DEBUG][SimeDetail.vue][getDataVolumeGraph] START...", Date());
				console.log("getDataVolumeGraph => period", period);
			}

			let cardStatus = cardOpenStatusDirective.getOpenCardStatus()["dataVolume-chart-collapse"];
			if (cardStatus) {
				await searchDetailService.getDataVolumeGraphByIMSI(period.toLowerCase(), this.imsi).then(res => {
					let xLabel = [];
					let dataVolume = [];
					for (let item in res) {
						if (res[item]["Date"]) {
							xLabel.push(res[item]["Date"]);
						}
						if (res[item]["Data Volume"]) {
							dataVolume.push(res[item]["Data Volume"].split(" ")[0]);
						}
					}
					this.chartXLabel = xLabel;
					this.chartDataVolume = dataVolume;
					this.$root.$emit("dataVolumeTrendByPeriod", xLabel, dataVolume);
					this.getDataVolumeAverage();
				});
			}

			if (process.env.NODE_ENV === "dev")
				console.warn("[DEBUG][SimeDetail.vue][getDataVolumeGraph] END...", Date());
		},

		async getDataVolumeAverage() {
			if (process.env.NODE_ENV === "dev")
				console.warn("[DEBUG][SimeDetail.vue][getDataVolumeAverage] START...", Date());

			let cardStatus = cardOpenStatusDirective.getOpenCardStatus()["dataVolume-chart-collapse"];
			if (cardStatus) {
				await searchDetailService.getDataVolumeAverageByIMSI(this.imsi).then(res => {
					//console.log('getDataVolumeAverageByIMSI:',res);
					searchDetailService.getDataVolumeBillingPeriodByIMSI(this.imsi).then(res1 => {
						//console.log('getDataVolumeBillingPeriodByIMSI:',res1);
						let data = {
							yearlyV: res ? res[0]["Data Volume this year (MB)"] : "",
							yearlyP: res ? res[0]["Year Percent Change"] : "",
							monthlyV: res ? res[0]["Data Volume this month (MB)"] : "",
							monthlyP: res ? res[0]["Month Percent Change"] : "",
							dailyV: res ? res[0]["Data Volume today (MB)"] : "",
							dailyP: res ? res[0]["Day Percent Change"] : "",
							//billingP: res1 ? res1[0]["Data Usage for Current Billing Period"] : "",
							//billingV: res1 ? res1[0]["Current Billing Period Percent Change"] : "",
						};
						this.$root.$emit("dataVolumeAverageByIMSI", data);

						if (process.env.NODE_ENV === "dev")
							console.warn("[DEBUG][SimeDetail.vue][getDataVolumeAverage] END...", Date());
					});
				});
			}
		},

		async getDataVolumeByRegion(period) {
			if (process.env.NODE_ENV === "dev")
				console.warn("[DEBUG][SimeDetail.vue][getDataVolumeByRegion] START...", Date());

			let cardStatus = cardOpenStatusDirective.getOpenCardStatus()["dataVolume-map-collapse"];
      //debugger
			if (cardStatus) {
				await searchDetailService.getDataVolumeByRegionByIMSI(period.toLowerCase(), this.imsi).then(res => {
					this.$root.$emit("dataVolumeTrendByRegion", res);
					if (process.env.NODE_ENV === "dev")
						console.warn("[DEBUG][SimeDetail.vue][getDataVolumeByRegion] END...", Date());
				});
			}
		},

		async getSimDetailTooltips() {
			if (process.env.NODE_ENV === "dev")
				console.warn("[DEBUG][SimeDetail.vue][getSimDetailTooltips] START...", Date());

			let dashboardToolTip = configDirective.getDashboardToolTip();
			let searchDetailToolTip = configDirective.getSearchDetailToolTip();
			this.dataSessionToolTip = dashboardToolTip ? dashboardToolTip["data_session_tooltip"] : "";
			this.dataVolumeToolTip = dashboardToolTip ? dashboardToolTip["data_volume_tooltip"] : "";
			this.alertsToolTip = dashboardToolTip ? dashboardToolTip["alerts_tooltip"] : "";
			this.zeroSessionsToolTip = dashboardToolTip ? dashboardToolTip["zero_sessions_tooltip"] : "";
			this.dataVolumeByPeriodToolTip = searchDetailToolTip ? searchDetailToolTip["datavolume_chart_tooltip"] : "";
			this.dataVolumeByRegionToolTip = dashboardToolTip ? dashboardToolTip["data_volume_by_region_tooltip"] : "";
			this.currentDataSessionToolTip = searchDetailToolTip
				? searchDetailToolTip["current_data_sessions_tooltip"]
				: "";
			this.stateToolTip = searchDetailToolTip ? searchDetailToolTip["state_tooltip"] : "";
			this.dataManagementToolTip = searchDetailToolTip ? searchDetailToolTip["watchdog_alerts_tooltip"] : "";

			if (process.env.NODE_ENV === "dev")
				console.warn("[DEBUG][SimeDetail.vue][getSimDetailTooltips] END...", Date());
		},

		async changeDataManagementTab(event) {
			if (process.env.NODE_ENV === "dev")
				console.warn("[DEBUG][SimeDetail.vue][changeDataManagementTab] START...", Date());

			let cardStatus = cardOpenStatusDirective.getOpenCardStatus()["topMetrics-collapse"];
			if (cardStatus) {
				event = event.replace(/ /g, "");
				if (event === "Alerts") {
					searchDetailService.getDataManagementAlertsByIMSI(event, this.imsi).then(res => {
						this.$root.$emit("fetchDataManagement", res);
					});
				} else if (event === "DataMonitors") {
					searchDetailService.getDataManagementMonitorsByIMSI(this.imsi).then(res => {
						this.$root.$emit("fetchDataManagement", res);
					});
				} else if (event === "DataPlans") {
					searchDetailService.getDataManagementDataPlansByIMSI(this.imsi).then(res => {
						this.$root.$emit("fetchDataManagement", res);
					});
				}
			}
			if (process.env.NODE_ENV === "dev")
				console.warn("[DEBUG][SimeDetail.vue][changeDataManagementTab] STOP...", Date());
		},

		async changeSessionTab(event) {
			this.getDataSessionsByIMSI(event.toLowerCase());
		},

		async changeVolumeTab(event) {
			this.getDataVolumeByIMSI(event.toLowerCase());
		},

		async changeAlertsTab(event) {
			this.getAlertsByIMSI(event.toLowerCase());
		},

		async changeZeroSessionsTab(event) {
			this.getZeroSessionsByIMSI(event.toLowerCase());
		},
	},
	watch: {
		$route: "getCurrentURL",
	},
};
</script>
